import {
    IQueryParams,
    StoreCategory,
    searchStores,
} from "tsi-common-react/src/api/retail";
import { StoreSearchFilters } from "tsi-common-react/src/apps/retail/models";
import {
    ILocation,
    IRetailStoreWithDistance,
} from "tsi-common-react/src/models/location";
import {
    IProductUUID,
    ISyncStoreID,
    isoMiles,
} from "tsi-common-react/src/models/nominals";
import { check } from "tsi-common-react/src/models/utils";
import { ajax } from "tsi-common-react/src/utils/ajax";
import {
    isLatLngLocation,
    isZipLocation,
} from "tsi-common-react/src/utils/guards";

import { CMSFlagshipStorePage, CMSFlagshipStorePagesAPIPage } from "./models";

const buildBaseQuery = (location: ILocation): IQueryParams | null => {
    if (isLatLngLocation(location)) {
        return {
            origLat: location.lat,
            origLng: location.lng,
        };
    }
    // search by zip
    if (isZipLocation(location)) {
        return {
            q: location.zip,
        };
    }
    return null;
};

export const getStores = async (
    location: ILocation,
    filters: StoreSearchFilters,
    product?: IProductUUID | null,
) => {
    const baseQuery = buildBaseQuery(location);
    if (!baseQuery) {
        return {
            allStores: [],
            flagshipStores: [],
            storesWithProduct: [],
        };
    }
    const allStores = await searchStores({
        ...baseQuery,
        distance: isoMiles.unwrap(filters.distance),
        has_slot_tempur: "Y",
    });
    const flagshipStores = await searchStores({
        ...baseQuery,
        category: StoreCategory.FLAGSHIP,
        has_slot_tempur: "Y",
    });
    const storesWithProduct = product
        ? await searchStores({
              ...baseQuery,
              product: product,
              has_slot_tempur: "Y",
          })
        : [];
    return {
        allStores: allStores,
        flagshipStores: flagshipStores,
        storesWithProduct: storesWithProduct,
    };
};

export const getStoreByExternalID = async (
    location: ILocation,
    externalID: ISyncStoreID,
): Promise<ReadonlyArray<IRetailStoreWithDistance>> => {
    const baseQuery = buildBaseQuery(location);
    if (!baseQuery) {
        return [];
    }
    return searchStores({
        ...baseQuery,
        external_id: externalID,
    });
};

export const getFlagshipStorePage = async (
    externalID: ISyncStoreID,
): Promise<CMSFlagshipStorePage> => {
    const resp = await ajax
        .get(`/api/cms/v2/flagship-pages/${externalID}/`)
        .set("Accept", "application/json");
    return check(CMSFlagshipStorePage.decode(resp.body));
};

export const getAllFlagshipStorePages = async (): Promise<
    CMSFlagshipStorePage[]
> => {
    const loadPage = async (offset = 0) => {
        const resp = await ajax
            .get("/api/cms/v2/flagship-pages/")
            .query({
                offset: offset,
            })
            .set("Accept", "application/json");
        return check(CMSFlagshipStorePagesAPIPage.decode(resp.body));
    };
    let results = await loadPage();
    let pages: CMSFlagshipStorePage[] = results.items;
    while (pages.length < results.meta.total_count) {
        results = await loadPage(pages.length);
        pages = pages.concat(results.items);
    }
    return pages;
};
